import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Company } from '@src/app/core/interfaces/common/company';
import { Option } from '@src/app/core/interfaces/common/option';
import { ResultObject } from '@src/app/core/interfaces/common/location/result-object.dto';
import { FormErrorHandlerService } from '@src/app/core/services/helpers/form-error-handler.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { CompanyService } from '@src/app/core/services/http/company.service';
import { TenantService } from '@src/app/core/services/http/tenant.service';
import { AppState } from '@src/app/state';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { catchError, map, of } from 'rxjs';

@Component({
  selector: 'app-transfer-company-modal',
  standalone: true,
  templateUrl: './transfer-company-modal.component.html',
  styleUrls: ['./transfer-company-modal.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class TransferCompanyModalComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @Output() transferCompleted = new EventEmitter<void>();

  company: Company | null = null;
  currentTenantId: string | null = null;
  currentTenantName: string | null = null;
  transferForm!: FormGroup;
  tenants$ = of<Option[]>([]);
  override isLoading = false;

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private tenantService: TenantService,
    private toastHelper: ToastHelperService,
    private formErrorHandler: FormErrorHandlerService
  ) {
    super(translateService, store);
    this.initializeForm();
  }

  private initializeForm(): void {
    this.transferForm = this.formBuilder.group({
      targetTenantId: [null, Validators.required],
    });
  }

  async open(company: Company): Promise<void> {
    this.company = company;
    this.currentTenantId = null;
    this.currentTenantName = null;
    this.transferForm.reset();
    this.isLoading = false;

    // Abrir modal primeiro
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'modal-transfer-company', false, true);

    // Carregar empresa completa para obter tenantId atual, depois carregar tenants
    if (company.id) {
      this.loadCompanyDetails(company.id);
    } else {
      // Se não tiver ID, carregar tenants diretamente
      this.loadTenants();
    }
  }

  private loadCompanyDetails(companyId: string): void {
    this.companyService.getCompanyById(companyId).subscribe({
      next: company => {
        // Verificar se a empresa tem tenantId na resposta
        // A interface Company pode não ter tenantId, mas a resposta da API pode incluir
        const companyWithTenant = company as Company & { tenantId?: string };
        if (companyWithTenant?.tenantId) {
          this.currentTenantId = companyWithTenant.tenantId;
          this.loadCurrentTenantName(this.currentTenantId);
        }
        // Carregar tenants após obter tenantId atual (ou mesmo se não tiver)
        this.loadTenants();
      },
      error: error => {
        console.error('Erro ao carregar detalhes da empresa:', error);
        // Mesmo com erro, tentar carregar tenants
        this.loadTenants();
      },
    });
  }

  private loadCurrentTenantName(tenantId: string): void {
    this.tenantService.getTenantById(tenantId).subscribe({
      next: tenant => {
        if (tenant) {
          this.currentTenantName = tenant.name;
        }
      },
      error: error => {
        console.error('Erro ao carregar nome do tenant atual:', error);
      },
    });
  }

  private loadTenants(): void {
    this.tenants$ = this.tenantService.getTenants({ Page: 1, PageSize: 1000 }).pipe(
      map(response => {
        if (!response || !response.data) {
          return [];
        }

        // Filtrar tenant atual da lista
        const tenants = response.data
          .filter(tenant => tenant.id !== this.currentTenantId)
          .map(tenant => ({
            label: tenant.name,
            value: tenant.id || '',
            data: tenant,
          }));

        return tenants;
      }),
      catchError(error => {
        console.error('Erro ao carregar tenants:', error);
        this.toastHelper.showToastError(
          'Erro ao carregar lista de tenants',
          error?.errors?.length ? error.errors[0].message : 'Erro na requisição'
        );
        return of([]);
      })
    );
  }

  isFormValid(): boolean {
    const control = this.transferForm.get('targetTenantId');
    const hasValue = !!control?.value;
    const isValid = this.transferForm.valid;

    return isValid && hasValue;
  }

  submitAction(): void {
    if (!this.isFormValid() || !this.company?.id) {
      return;
    }

    const targetTenantId = this.transferForm.get('targetTenantId')?.value;
    this.isLoading = true;

    this.companyService.transferCompany(this.company.id, targetTenantId).subscribe({
      next: result => {
        this.isLoading = false;

        // Se chegou no next sem erro, a operação foi bem-sucedida
        // O VaultApiService retorna null quando response.status é true e data é null
        this.toastHelper.showToastSuccess('Empresa transferida com sucesso', '');
        this.transferCompleted.emit();
        this.closeModal();
      },
      error: error => {
        this.isLoading = false;
        console.error('Erro ao transferir empresa:', error);
        this.formErrorHandler.showToast(error, 'Erro ao transferir empresa');
      },
    });
  }

  closeModal(): void {
    this.dialogRef?.close();
    this.transferForm.reset();
    this.company = null;
    this.currentTenantId = null;
    this.currentTenantName = null;
  }
}
